import { ICredit } from '../../../interfaces/ICredit';

/**
 * How a credit's source invoice should be named on screen.
 *
 * `unlinked` means the credit genuinely comes off no invoice. `unnamed` means it comes off
 * one that the current payload did not name — the id is known, the number is not — which is a
 * gap in what was fetched, not a gap in the data.
 */
export type TCreditInvoiceLabelKind = 'named' | 'unnamed' | 'unlinked';

export interface ICreditInvoiceLabel {
  kind: TCreditInvoiceLabelKind;
  /** Ready to display. Never empty. */
  text: string;
}

/**
 * Works out what can honestly be said about the invoice a credit comes off.
 *
 * `credits.invoice_id` is the link — there is no `invoice_number` column on the credit
 * (`entities/hydra/Credit.ts`), so `ICredit.invoiceNumber` is a transient the create modals
 * set and `credit.invoice` is populated only by the one query that joins it
 * (`routers/creditNotes.ts`, the single-note fetch). Reading only those two therefore reports
 * a linked credit as unlinked whenever it was rendered from a payload that joined nothing —
 * most visibly a credit just added in the browser, which carries `invoiceId` and a null
 * `invoiceNumber` and is never refetched before it is displayed.
 *
 * So the id is what decides linked or not, and the number is only ever used to name it.
 *
 * @param credit - The credit being rendered.
 * @returns The label kind and the text to show.
 */
export const getCreditInvoiceLabel = (credit: ICredit): ICreditInvoiceLabel => {
  const invoiceNumber = credit.invoiceNumber ?? credit.invoice?.invoiceNumber ?? null;

  if (invoiceNumber !== null && `${invoiceNumber}` !== '') {
    return { kind: 'named', text: `${invoiceNumber}` };
  }

  if (credit.invoiceId !== null && credit.invoiceId !== undefined) {
    return { kind: 'unnamed', text: `Invoice #${credit.invoiceId}` };
  }

  return { kind: 'unlinked', text: 'UNLINKED' };
};
